import { useState } from "react";
import { SegmentedControl } from "../../../design-v2";
import {
  CYCLE_ROLE,
  WEEK_STATUS,
  formatRatioPercent,
  formatWeekStatus,
} from "../domain/insightsPresentation";
import { COMPACT_WEEK_LABELS_AFTER, axisLabelIndexes, integerTicks } from "../domain/chartAxes";
import InsightsLineChart from "./InsightsLineChart";

/*
 * Adherence presentation. Every count and ratio is the API's (§7.1): `due`,
 * `completed`, `extra`, `notYetDue`, `planAdherence`, `totalExecuted`. The only
 * arithmetic here is display arithmetic on those raw fields: dots per state,
 * the missed remainder of what is already due (due − completed) and the
 * planned line (due + not yet due).
 */

function readCount(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

/* Weekly workouts ----------------------------------------------------------- */

function describeWorkoutWeek(column) {
  const workouts = column.week.workouts || {};
  const due = readCount(workouts.due);
  const completed = readCount(workouts.completed);
  const extra = readCount(workouts.extra);
  const notYetDue = readCount(workouts.notYetDue);
  const parts = [`${column.longLabel}, ${formatWeekStatus(column.status)}`];
  parts.push(due > 0 ? `${completed} of ${due} due workouts completed` : "no workouts due");
  if (extra > 0) parts.push(`${extra} extra`);
  if (notYetDue > 0) parts.push(`${notYetDue} not yet due`);
  return parts.join(", ");
}

function WorkoutDots({ workouts }) {
  const due = readCount(workouts?.due);
  const completed = Math.min(readCount(workouts?.completed), due);
  const missed = due - completed;
  const extra = readCount(workouts?.extra);
  const notYetDue = readCount(workouts?.notYetDue);
  const dots = [
    ...Array(completed).fill("completed"),
    ...Array(extra).fill("extra"),
    ...Array(missed).fill("missed"),
    ...Array(notYetDue).fill("remaining"),
  ];
  return (
    <span className="lz-insights-dots__stack" aria-hidden="true">
      {dots.map((state, index) => (
        <span key={index} className="lz-insights-dot" data-state={state} />
      ))}
    </span>
  );
}

// Current Analysis: one dot per workout, per cycle week (hub + Adherence
// mockups). Dots stack up from a week axis (numbers under the dots, "Weeks"
// under the numbers), the cycles named above their first week. Long
// timelines (two 6- or 8-week cycles…) drop to the bare week number; columns
// shrink to 12px so two 8-week cycles fit a 320px phone, and anything wider
// scrolls inside the chart, never the page.
function segmentWeeks(columns, role) {
  return columns.filter((column) => column.segment === role).length;
}

export function WeeklyWorkoutsDots({ columns }) {
  const boundary = columns.findIndex((column, index) => index > 0
    && column.segment === CYCLE_ROLE.ACTIVE
    && columns[index - 1].segment === CYCLE_ROLE.PREVIOUS);
  const dense = columns.length > COMPACT_WEEK_LABELS_AFTER;
  const segments = [
    [CYCLE_ROLE.PREVIOUS, "Prev cycle", segmentWeeks(columns, CYCLE_ROLE.PREVIOUS)],
    [CYCLE_ROLE.ACTIVE, "Curr cycle", segmentWeeks(columns, CYCLE_ROLE.ACTIVE)],
  ].filter(([, , count]) => count > 0);
  return (
    <div className="lz-insights-dots" data-testid="weekly-workouts-dots" data-dense={dense || undefined}>
      <div className="lz-insights-dots__scroll">
        <div className="lz-insights-dots__track">
          <p className="lz-insights-dots__segments" aria-hidden="true">
            {segments.map(([role, name, count]) => (
              <span
                key={role}
                className="lz-insights-dots__segment"
                data-segment={role}
                style={{ flexGrow: count, "--lz-insights-segment-weeks": count }}
              >
                {name}
              </span>
            ))}
          </p>
          <ol className="lz-insights-dots__weeks" aria-label="Weekly workouts">
            {columns.map((column, index) => (
              <li
                key={column.key}
                className={[
                  "lz-insights-dots__week",
                  index === boundary ? "lz-insights-dots__week--boundary" : "",
                  column.status === WEEK_STATUS.IN_PROGRESS ? "lz-insights-dots__week--current" : "",
                ].filter(Boolean).join(" ")}
                data-status={column.status}
                aria-label={describeWorkoutWeek(column)}
              >
                <WorkoutDots workouts={column.week.workouts} />
                <span className="lz-insights-dots__label" aria-hidden="true">
                  {dense ? column.shortLabel || column.label : column.label}
                </span>
              </li>
            ))}
          </ol>
          <p className="lz-insights-axis-title" aria-hidden="true">Weeks</p>
        </div>
      </div>
      <WorkoutLegend />
    </div>
  );
}

// Historical calendar weeks can number in the hundreds: stacked bars instead
// of dots, same states. Integer workout ticks on the left; every week keeps
// its bar while only a handful of dates are labelled.
const BAR_AXIS_LABELS = 5;

export function WeeklyWorkoutsBars({ columns }) {
  const max = Math.max(1, ...columns.map((column) => {
    const workouts = column.week.workouts || {};
    return readCount(workouts.due) + readCount(workouts.extra) + readCount(workouts.notYetDue);
  }));
  const ticks = integerTicks(max);
  const top = ticks[ticks.length - 1];
  const percent = (value) => `${(value / top) * 100}%`;
  const labelled = axisLabelIndexes(columns.length, BAR_AXIS_LABELS);
  return (
    <div className="lz-insights-bars" data-testid="weekly-workouts-bars">
      <div className="lz-insights-bars__plot">
        <span className="lz-insights-bars__y" aria-hidden="true" data-testid="weekly-workouts-y-axis">
          {ticks.map((tick) => (
            <span key={tick} className="lz-insights-bars__tick" style={{ bottom: percent(tick) }}>{tick}</span>
          ))}
        </span>
        <div className="lz-insights-bars__area">
          {ticks.map((tick) => (
            <span key={tick} className="lz-insights-bars__grid" style={{ bottom: percent(tick) }} aria-hidden="true" />
          ))}
          <ol className="lz-insights-bars__weeks" aria-label="Weekly workouts">
            {columns.map((column) => {
              const workouts = column.week.workouts || {};
              const due = readCount(workouts.due);
              const completed = Math.min(readCount(workouts.completed), due);
              const parts = [
                ["completed", completed],
                ["extra", readCount(workouts.extra)],
                ["missed", due - completed],
                ["remaining", readCount(workouts.notYetDue)],
              ];
              return (
                <li key={column.key} className="lz-insights-bars__week" aria-label={describeWorkoutWeek(column)}>
                  <span className="lz-insights-bars__stack" aria-hidden="true">
                    {parts.filter(([, value]) => value > 0).map(([state, value]) => (
                      <span
                        key={state}
                        className="lz-insights-bars__part"
                        data-state={state}
                        style={{ height: percent(value) }}
                      />
                    ))}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
        <p className="lz-insights-bars__axis" aria-hidden="true" data-axis="x">
          {labelled.map((index) => (
            <span key={columns[index].key} style={{ left: `${((index + 0.5) / columns.length) * 100}%` }}>
              {columns[index].label}
            </span>
          ))}
        </p>
        <p className="lz-insights-axis-title lz-insights-bars__title" aria-hidden="true">Weeks</p>
      </div>
      <WorkoutLegend />
    </div>
  );
}

function WorkoutLegend() {
  return (
    <ul className="lz-insights-legend" aria-label="Legend">
      <li><span className="lz-insights-dot" data-state="completed" aria-hidden="true" />Completed</li>
      <li><span className="lz-insights-dot" data-state="extra" aria-hidden="true" />Extra</li>
      <li><span className="lz-insights-dot" data-state="missed" aria-hidden="true" />Missed</li>
      <li><span className="lz-insights-dot" data-state="remaining" aria-hidden="true" />Not yet due</li>
    </ul>
  );
}

/* Weekly sets ---------------------------------------------------------------- */

export function WeeklySetsChart({ columns, highlightIndex = null, title = null }) {
  // An upcoming week has nothing performed yet: no completed / extra point.
  const performed = (column, read) => (column.status === WEEK_STATUS.UPCOMING ? null : read(column.week.sets || {}));
  const series = [
    {
      key: "planned",
      name: "Planned sets",
      tone: "muted",
      secondary: true,
      values: columns.map((column) => readCount(column.week.sets?.due) + readCount(column.week.sets?.notYetDue)),
    },
    {
      key: "executed",
      name: "Completed plus extra sets",
      tone: "info",
      marker: "circle",
      values: columns.map((column) => performed(column, (sets) => readCount(sets.completed) + readCount(sets.extra))),
    },
    {
      key: "completed",
      name: "Completed prescribed sets",
      tone: "action",
      marker: "circle",
      area: true,
      values: columns.map((column) => performed(column, (sets) => readCount(sets.completed))),
    },
  ];
  return (
    <div className="lz-insights-sets">
      {title ? <h3 className="lz-insights-subtitle">{title}</h3> : null}
      <InsightsLineChart
        columns={columns}
        series={series}
        highlightIndex={highlightIndex}
        summary="Weekly sets: planned, completed prescribed, and completed plus extra"
        xTitle="Weeks"
        yTitle="Sets"
        testId="weekly-sets-chart"
      />
      <ul className="lz-insights-legend" aria-hidden="true">
        <li data-tone="action"><span className="lz-insights-legend__swatch" />Completed</li>
        <li data-tone="muted"><span className="lz-insights-legend__swatch lz-insights-legend__swatch--secondary" />Planned</li>
        <li data-tone="info"><span className="lz-insights-legend__swatch" />Extra</li>
      </ul>
    </div>
  );
}

/* Weekly workouts | sets ----------------------------------------------------------- */

export const WEEKLY_VIEW = Object.freeze({ WORKOUTS: "workouts", SETS: "sets" });

// One weekly chart at a time behind [ WORKOUTS | SETS ]. Both charts read the
// columns already loaded, so switching is local and instant. Both views share
// one grid cell (the hidden one keeps its box, inert and invisible), so the
// slot is always as tall as the taller view and the card never resizes when
// the mode changes; legends sit at the bottom of the slot in both modes.
export function WeeklyAdherenceCharts({ columns, workoutsAs = "bars", highlightIndex = null }) {
  const [view, setView] = useState(WEEKLY_VIEW.WORKOUTS);
  const views = [
    [WEEKLY_VIEW.WORKOUTS, workoutsAs === "dots"
      ? <WeeklyWorkoutsDots columns={columns} />
      : <WeeklyWorkoutsBars columns={columns} />],
    [WEEKLY_VIEW.SETS, <WeeklySetsChart columns={columns} highlightIndex={highlightIndex} />],
  ];
  return (
    <div className="lz-insights-weekly" data-testid="weekly-adherence">
      <SegmentedControl
        label="Weekly chart"
        className="lz-insights-mode-switch"
        value={view}
        onChange={setView}
        options={[
          { value: WEEKLY_VIEW.WORKOUTS, label: "Workouts" },
          { value: WEEKLY_VIEW.SETS, label: "Sets" },
        ]}
      />
      <div className="lz-insights-weekly__slot">
        {views.map(([key, chart]) => (
          <div
            key={key}
            className="lz-insights-weekly__view"
            data-view={key}
            data-active={view === key || undefined}
            aria-hidden={view === key ? undefined : true}
            inert={view === key ? undefined : ""}
          >
            {chart}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Rings ------------------------------------------------------------------------ */

const RING_RADIUS = 42;

// Plan Adherence ring: one lime ring, the ratio. Total Executed: two concentric
// rings (Option A). Inner lime = the Plan Adherence share only; outer cyan,
// thinner = the Extra share only (totalExecuted − planAdherence, both API
// ratios); the centre shows the total. The outer arc caps at one full turn.
const DUAL_OUTER_RADIUS = 51.5;
const DUAL_VIEWBOX = "-6 -6 112 112";

function ringCircumference(radius) {
  return 2 * Math.PI * radius;
}

function arc(fraction, radius = RING_RADIUS) {
  const clamped = Math.max(0, Math.min(1, fraction));
  const circumference = ringCircumference(radius);
  return `${clamped * circumference} ${circumference}`;
}

export function AdherenceRing({ variant, planAdherence, totalExecuted, label, caption, testId }) {
  const dual = variant === "total";
  const ratio = dual ? totalExecuted : planAdherence;
  const percent = formatRatioPercent(ratio);
  const noData = percent === null;
  const plan = Number(planAdherence) || 0;
  const total = Number(totalExecuted) || 0;
  const extraShare = dual ? Math.max(0, total - plan) : 0;
  const innerRadius = RING_RADIUS;
  return (
    <div className="lz-insights-ring" data-variant={variant} data-testid={testId} data-empty={noData || undefined}>
      <div className="lz-insights-ring__dial">
        <svg viewBox={dual ? DUAL_VIEWBOX : "0 0 100 100"} aria-hidden="true" focusable="false">
          <circle className="lz-insights-ring__track" cx="50" cy="50" r={innerRadius} />
          {dual ? (
            <circle className="lz-insights-ring__track lz-insights-ring__track--outer" cx="50" cy="50" r={DUAL_OUTER_RADIUS} />
          ) : null}
          {!noData ? (
            <circle
              className="lz-insights-ring__arc"
              cx="50"
              cy="50"
              r={innerRadius}
              strokeDasharray={arc(plan, innerRadius)}
              transform="rotate(-90 50 50)"
            />
          ) : null}
          {!noData && extraShare > 0 ? (
            <circle
              className="lz-insights-ring__arc lz-insights-ring__arc--extra"
              cx="50"
              cy="50"
              r={DUAL_OUTER_RADIUS}
              strokeDasharray={arc(extraShare, DUAL_OUTER_RADIUS)}
              transform="rotate(-90 50 50)"
            />
          ) : null}
        </svg>
        <span className="lz-insights-ring__value">
          <span className="lz-insights-ring__percent">{noData ? "—" : percent}</span>
        </span>
      </div>
      <p className="lz-insights-ring__label">{label}</p>
      <p className="lz-insights-ring__caption">{caption}</p>
    </div>
  );
}

/* Prescribed / extra breakdown -------------------------------------------------- */

function BreakdownColumn({ tone, title, rows, testId }) {
  return (
    <div className="lz-insights-breakdown__col" data-tone={tone} data-testid={testId}>
      <p className="lz-insights-breakdown__title">{title}</p>
      {rows.map(([key, value, unit]) => (
        <p key={key} className="lz-insights-breakdown__row">
          <span className="lz-insights-breakdown__value">{value}</span> {unit}
        </p>
      ))}
    </div>
  );
}

/*
 * The Execution summary of one adherence scope (a Current Analysis cycle or a
 * historical timeframe): the two headline rings (sets, §7.1) and a compact
 * prescribed / extra breakdown of the same totals.
 */
export function ExecutionSummary({ totals, headline }) {
  const sets = totals?.sets || {};
  const workouts = totals?.workouts || {};
  const setsDue = readCount(sets.due);
  const workoutsDue = readCount(workouts.due);
  const setsCompleted = readCount(sets.completed);
  const setsExtra = readCount(sets.extra);
  const workoutsCompleted = readCount(workouts.completed);
  const workoutsExtra = readCount(workouts.extra);

  return (
    <div className="lz-insights-execution" data-testid="execution-summary">
      <div className="lz-insights-rings">
        <AdherenceRing
          variant="plan"
          planAdherence={headline?.planAdherence}
          totalExecuted={headline?.totalExecuted}
          label="Plan Adherence"
          caption="Prescribed only"
          testId="ring-plan-adherence"
        />
        <AdherenceRing
          variant="total"
          planAdherence={headline?.planAdherence}
          totalExecuted={headline?.totalExecuted}
          label="Total Executed"
          caption="Prescribed + extra"
          testId="ring-total-executed"
        />
      </div>
      <div className="lz-insights-breakdown">
        <BreakdownColumn
          tone="action"
          title="Prescribed"
          testId="breakdown-prescribed"
          rows={[
            ["workouts", `${workoutsCompleted} / ${workoutsDue}`, "workouts"],
            ["sets", `${setsCompleted} / ${setsDue}`, "sets"],
          ]}
        />
        <BreakdownColumn
          tone="info"
          title="Extra"
          testId="breakdown-extra"
          rows={[
            ["workouts", `+${workoutsExtra}`, workoutsExtra === 1 ? "workout" : "workouts"],
            ["sets", `+${setsExtra}`, setsExtra === 1 ? "set" : "sets"],
          ]}
        />
      </div>
    </div>
  );
}
